/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import Coin from 'components/Coin.jsx';
import CurrentUser from 'auth/CurrentUser';
import Avatar from 'components/Avatar.jsx';
import Buttons from 'components/Buttons.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupMembership from 'entities/GroupMembership';
import { getPropClassesFromInstance } from 'lib/getPropClasses';
import MediaObject from 'components/MediaObject.jsx';
import OrgRoleDropdown from 'groups/components/OrgRoleDropdown.jsx';
import RelationshipModal from 'user/components/RelationshipModal.jsx';
import StartChatButton from 'user/components/StartChatButton.jsx';
import Text from 'components/Text.jsx';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities.jsx';
import Time from 'components/Time.jsx';
import GroupMembersTabUserActions from 'user/components/GroupMembersTabUserActions.jsx';
import { pushRouterToChatWithMember } from 'lib/ChatRouteHelper';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { showRecent, hideRecent } from 'reducers/recentMessagesModal';

import AddGroupOwnerModalConfirm from './AddGroupOwnerModalConfirm.jsx';

import './styles/GroupMembersTabListItem.scss';

type Props = {
  group: Group,
  query?: string,
  selected?: boolean,
  highlight?: boolean,
  member: GroupMembership,
  isLinkedToOrg: boolean,
  displayBy?: 'firstName' | 'lastName' | 'subscribedAt',
  showRecent: () => void,
  hideRecent: () => void,
  className?: string,
}

export class GroupMembersTabListItem extends React.Component {
  props: Props;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static propClasses = {
    selected: 'GroupMembersTabListItem-selected',
    highlight: 'GroupMembersTabListItem-highlight',
  };

  static defaultProps = {
    selected: false,
    highlight: false,
  };

  isCurrentUser = () => {
    return this.props.member.getUser().id === CurrentUser.getUser().id;
  };

  startChatWithMember = () => {
    const { group, member } = this.props;

    pushRouterToChatWithMember(this.context.router, this.props.showRecent, this.props.hideRecent, member.getUser(), group);
  };

  renderActions = () => {
    const { member } = this.props;
    const { group } = this.props;

    return (
      <div className="GroupMembersTabListItem-actions">
        <Buttons align="right">
          {member.getCanChat() &&
            <StartChatButton
              className="GroupMembersTabListItem-chatButton"
              useGlyph
              subscriber={this.props.member.getUser()}
            />
          }
          <GroupMembersTabUserActions
            className="GroupMembersTabListItem-actionButton"
            tooltip="More Actions"
            tooltipPlacement="top"
            level="text"
            groupToAdmin={group}
            groupMembership={member}
            showViewProfile
          />
        </Buttons>
      </div>
    );
  };

  renderInfo = () => {
    if (this.isCurrentUser()) {
      return (
        <div className="GroupMembersTabListItem-actions">
          {this.renderUnder13Label()}
        </div>
      );
    } else {
      return this.renderActions();
    }
  };

  renderMakeOwnerDialog = () => {
    return (
      <AddGroupOwnerModalConfirm
        group={this.props.group}
        checkedIds={[this.props.member.getUser().id]}
        userName={this.props.member.getUser().getName()}
      />
    );
  };

  renderName = () => {
    const { member, query } = this.props;
    const subscriber = member.getUser();

    const actionClass = ((): ?string => {
      if (member.getCanChat() || this.isCurrentUser()) {
        return 'GroupMembersTabListItem--actionable';
      } else {
        return null;
      }
    })();

    let name = subscriber.getFirstName() + ' ' + subscriber.getLastName();

    // U13 children have a "name" provided by the server, but no first/last name.
    if (name === ' ') {
      name = subscriber.getName();
    }
    let signature = ` (${subscriber.getSignature()})`;
    let note = this.isCurrentUser() ? 'You' : subscriber.getNote();
    const crownBadge = (
      <Coin className="GroupMembersTabListItem-badge" color="midnight" size="small">
        <Glyph name="crown" color="white" size="xxsmall" solo />
      </Coin>
    );

    if (query && query !== '') {
      const parser = TextEntities.createTextMatchingParser(query);

      if (subscriber.getSignature() && parser(signature).length > parser(name).length) {
        signature = <TextWithEntities text={signature} entities={parser(signature)} />;
      } else {
        signature = '';
      }
      if (note) {
        note = <TextWithEntities text={note} entities={parser(note)} />;
      }
      name = <TextWithEntities text={name} entities={parser(name)} />;
    }

    const nameView = this.isCurrentUser() ? (
      <MediaObject gutter="small" center>
        <div className="GroupMembersTabListItem-avatarBadgeWrap">
          <Avatar
            color="grey"
            text={subscriber.getInitials()}
            size="medium"
          />
          {member.isOwner() && crownBadge}
        </div>
        <Text
          className={cx('GroupMembersTabListItem-userName', actionClass)}
          truncate>
          {name}
          {query && signature}
        </Text>
      </MediaObject>
    ) :
      (
      <MediaObject gutter="small" center>
        <div className="GroupMembersTabListItem-avatarBadgeWrap">
          <Avatar
            colorHex={subscriber.getColor()}
            text={subscriber.getInitials()}
            size="medium"
          />
          {member.isOwner() && crownBadge}
        </div>
        <Text
          className={cx('GroupMembersTabListItem-userName', actionClass)}
          truncate>
          {name}
          {query && signature}
          <Text
            color="eclipse"
            size="xxsmall"
            truncate>
            {note}
          </Text>
        </Text>
      </MediaObject>
    );

    // wrap in an appropriate click handler
    let clickHandler = null;
    if (this.isCurrentUser()) {
      clickHandler = () => this.context.router.push('/settings');
    } else if (member.getCanChat()) {
      clickHandler = this.startChatWithMember;
    }
    return (
      <div onClick={clickHandler}>
        {nameView}
      </div>
    );
  };

  renderRoles = () => {
    const { group, member } = this.props;
    const orgAffView = member.getOrgAffiliationView();

    if (!orgAffView) {
      return null;
    }

    // remove admin from potential roles, this is an admin console feature.
    const potentialRoles = orgAffView.potential_roles.filter(potentialRole => {
      return potentialRole.role !== 'admin';
    });

    // if user can change role inside the admin console
    // then we disable changing role in the core app.
    // roles can still be initialized here (like non admin teachers can).
    const canChangeRoleInAdminConsole = !!(
      CurrentUser.getUser().hasFeature('web_admin_console') &&
      this.props.group.isStaffClass() &&
      this.props.group.getOrganization() &&
      this.props.group.getOrganization().get('paid')
    );

    const canHaveRoleChanged = !canChangeRoleInAdminConsole && orgAffView.can_have_role_changed;
    const user = member.getUser();

    return (
      <OrgRoleDropdown
        roles={orgAffView.roles}
        potentialRoles={potentialRoles}
        canHaveRoleInitialized={orgAffView.can_have_role_initialized}
        canHaveRoleChanged={canHaveRoleChanged}
        name={user.getFullName()}
        group={group}
        userId={user.id}
      />
    );
  };

  renderRelationshipDialog = () => {
    return (
      <RelationshipModal
        groupToAdmin={this.props.group}
        groupMember={this.props.member}
        userId={this.props.member.getUser().id}
      />
    );
  };

  renderUnder13Label = () => {
    // return <StatusLabel>Under 13</StatusLabel>;
    return null;  // FIXME: alter API to send this data
  };

  render() {
    const { member, isLinkedToOrg, className, group } = this.props;

    return (
      <tr
        className={cx(
          'GroupMembersTabListItem',
          className,
          getPropClassesFromInstance(this),
        )}>
        <td className="GroupMembersTabListItem-name">
          {this.renderName()}
        </td>
        {isLinkedToOrg &&
          <td>
            {this.renderRoles()}
          </td>
        }
        {!group.isRoot() && !group.isStaffClass() /* createdAt is inaccurate for staff classes */ &&
          <td>
            <Time date={member.getCreatedAt()} month="short" day="numeric" year="numeric" />
          </td>
        }
        <td className="GroupMembersTabListItem-info">
          {this.renderInfo()}
        </td>
      </tr>
    );
  }
}

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators({
    showRecent,
    hideRecent,
  }, dispatch);

export default connect(undefined, mapDispatchToProps, undefined, { pure: false })(GroupMembersTabListItem);



// WEBPACK FOOTER //
// ./src/groups/components/GroupMembersTabListItem.jsx